import React from 'react';
import { cn } from '../lib/utils';

interface GlassCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'premium' | 'epic' | 'legendary';
  glow?: boolean;
  animated?: boolean;
  clickable?: boolean;
  onClick?: () => void;
}

const GlassCard: React.FC<GlassCardProps> = ({
  children,
  className,
  variant = 'default',
  glow = false,
  animated = true,
  clickable = false,
  onClick
}) => {
  const variants = {
    default: 'bg-glass-bg border-glass-border backdrop-blur-sm',
    premium: 'bg-gradient-to-br from-space-royal/20 to-space-star/20 border-space-star/30 backdrop-blur-md',
    epic: 'bg-gradient-to-br from-accent-violet/25 to-accent-pink/25 border-accent-violet/40 backdrop-blur-lg',
    legendary: 'bg-gradient-to-br from-accent-gold/30 to-accent-red/30 border-accent-gold/50 backdrop-blur-xl'
  };

  const glowStyles = {
    default: 'shadow-lg shadow-space-star/10',
    premium: 'shadow-2xl shadow-space-star/25 drop-shadow-lg',
    epic: 'shadow-2xl shadow-accent-violet/30 drop-shadow-xl',
    legendary: 'shadow-2xl shadow-accent-gold/40 drop-shadow-2xl'
  };

  return (
    <div
      className={cn(
        // Base styles
        'relative rounded-xl border transition-all duration-500 ease-out',
        variants[variant],
        
        // Glow effects
        glow && glowStyles[variant],
        
        // Animation styles
        animated && 'transform-gpu hover:scale-[1.02] hover:-translate-y-1',
        
        // Clickable styles
        clickable && 'cursor-pointer hover:border-opacity-60 active:scale-[0.98]',
        
        // Interactive effects
        'hover:shadow-lg hover:backdrop-blur-lg',
        
        // Advanced visual effects
        variant === 'premium' && 'hover:shadow-space-star/30',
        variant === 'epic' && 'hover:shadow-accent-violet/35',
        variant === 'legendary' && 'hover:shadow-accent-gold/45',
        
        className
      )}
      onClick={clickable ? onClick : undefined}
      style={{
        background: variant === 'legendary' 
          ? 'linear-gradient(135deg, rgba(245,158,11,0.3) 0%, rgba(239,68,68,0.3) 100%)'
          : undefined
      }}
    >
      {/* Inner glow effect */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 p-6">
        {children}
      </div>
      
      {/* Legendary particle effect overlay */}
      {variant === 'legendary' && (
        <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-2 h-2 bg-accent-gold rounded-full animate-pulse opacity-60" />
          <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-accent-gold rounded-full animate-ping opacity-40" />
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-accent-red rounded-full animate-pulse opacity-50" />
        </div>
      )}
    </div>
  );
};

export default GlassCard;